import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Clock } from "@phosphor-icons/react";
import FiltrosDashboard from "@/components/dashboard/FiltrosDashboard";
import ResumoExecutivo from "@/components/dashboard/ResumoExecutivo";
import AtencaoHoje from "@/components/dashboard/AtencaoHoje";
import { EvolucaoProducao, ExecutadoPlanejado } from "@/components/dashboard/ProducaoAvanco";
import ProdutividadeEquipes from "@/components/dashboard/ProdutividadeEquipes";
import { brDate } from "@/components/dashboard/util";
import "@/components/dashboard/dashboard.css";

const STORAGE = "dash_filtros_v2";

function lerFiltrosSalvos() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE)) || null; } catch { return null; }
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [filtros, setFiltros] = useState(lerFiltrosSalvos);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async (f) => {
    setLoading(true); setErro(null);
    try {
      const params = {};
      if (f) {
        params.inicio = f.inicio; params.fim = f.fim;
        params.contrato_ids = (f.contrato_ids || []).join(",");
        if (f.empresa_id) params.empresa_id = f.empresa_id;
      }
      const { data: d } = await api.get("/dashboard/v2", { params });
      setData(d);
      setFiltros(d.filtros);
      sessionStorage.setItem(STORAGE, JSON.stringify(d.filtros));
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não foi possível carregar o painel.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(filtros); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (patch) => carregar({ ...filtros, ...patch });
  const onLimpar = () => { sessionStorage.removeItem(STORAGE); carregar(null); };

  if (!data && loading) return <div style={{ padding: 32 }} data-testid="dash-carregando">Carregando painel…</div>;
  if (!data) return <div style={{ padding: 32, color: "#DC2626" }} data-testid="dash-erro">{erro}</div>;

  const { contexto, resumo, regras } = data;
  const geradoEm = new Date(contexto.gerado_em);

  return (
    <>
      <div className="page-header dash-header" data-testid="page-dashboard">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 className="font-display font-bold" style={{ fontSize: "1.5rem", lineHeight: 1.1 }}>Painel de Controle</h1>
            <div style={{ fontSize: "0.78rem", color: "#71717A", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }} data-testid="dash-atualizado-em">
              <Clock size={13} weight="bold" /> Dados atualizados em {geradoEm.toLocaleDateString("pt-BR")} às {geradoEm.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
              {loading && <span style={{ color: "#A1A1AA" }}> · atualizando…</span>}
              <span style={{ color: "#A1A1AA" }}>· {contexto.total_os_recorte} OS no recorte</span>
            </div>
          </div>
        </div>
        {(contexto.em_fase_testes || contexto.periodo_antes_virada) && (
          <div className="dash-faixa-testes" data-testid="dash-faixa-testes" title="Registros anteriores à data de virada são dados de teste e não representam resultados definitivos.">
            {contexto.em_fase_testes
              ? <>Sistema em fase de testes · virada operacional prevista para <strong>{brDate(contexto.data_virada)}</strong> · resultados não definitivos</>
              : <>Período inclui datas anteriores à virada de <strong>{brDate(contexto.data_virada)}</strong> — registros de teste misturados aos de produção</>}
          </div>
        )}
        <FiltrosDashboard filtros={filtros} opcoes={data.opcoes} contexto={contexto} onChange={onChange} onLimpar={onLimpar} onRefresh={() => carregar(filtros)} loading={loading} />
      </div>

      <div className="page-body dash-body" style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
        <ResumoExecutivo resumo={resumo} filtros={filtros} regras={regras} />
        <AtencaoHoje atencao={data.atencao} alertas={data.alertas} contexto={contexto} filtros={filtros} />
        <div className="dash-duas-colunas">
          <EvolucaoProducao evolucao={data.evolucao} filtros={filtros} />
          <ExecutadoPlanejado execPlanejado={data.exec_planejado} filtros={filtros} />
        </div>
        <ProdutividadeEquipes produtividade={data.produtividade} filtros={filtros} />
        <div className="dash-status-rodape" data-testid="dash-status-count">
          OS no recorte por status: {Object.entries(data.status_count).filter(([, v]) => v > 0).map(([k, v]) => `${k} ${v}`).join(" · ") || "nenhuma"} · Canceladas ficam fora de todos os indicadores.
        </div>
      </div>
    </>
  );
}
